import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { useWorkspace } from '../context/WorkspaceContext.jsx';
import { Skeleton } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import EventDetailModal from '../components/EventDetailModal.jsx';
import AppCalendar from '../components/AppCalendar.jsx';
import { cachedFetch, invalidate } from '../api/cache.js';

export default function Calendar() {
  const { user } = useAuth();
  const { isFunds, isTrade } = useWorkspace();
  const canEdit = user?.role === 'admin' || user?.role === 'manager';
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState('');
  // Defaults follow active workspace; user can still toggle everything.
  const [showTasks, setShowTasks] = useState(true);
  const [showContracts, setShowContracts] = useState(true);
  const [showSal, setShowSal] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (isFunds) {
      setShowSal(true);
      setShowContracts(false);
      setShowTasks(true);
    } else if (isTrade) {
      setShowSal(false);
      setShowContracts(true);
      setShowTasks(true);
    }
  }, [isFunds, isTrade]);

  async function loadEvents() {
    try {
      const ws = isFunds ? 'funds' : 'trade';
      const path = `/calendar?workspace=${ws}`;
      setEvents(await cachedFetch(path, () => api.get(path), { ttl: 30_000, force: true }));
    } catch (e) {
      setErr(e.message);
    }
  }

  useEffect(() => {
    setEvents(null);
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFunds, isTrade]);

  if (err) return <div className="alert error">{err}</div>;

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Calendar')}</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={showTasks} onChange={(e) => setShowTasks(e.target.checked)} />
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--primary)' }} /> {t('Tasks')}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={showContracts} onChange={(e) => setShowContracts(e.target.checked)} />
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--danger)' }} /> {t('Contract expiries')}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={showSal} onChange={(e) => setShowSal(e.target.checked)} />
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--success)' }} /> {t('Payment milestones')}
          </label>
        </div>
      </div>

      {!events ? (
        <div className="panel" style={{ padding: 24 }}>
          <Skeleton height={24} width="40%" />
          <div style={{ marginTop: 16 }}><Skeleton height={320} /></div>
        </div>
      ) : (
        <AppCalendar
          data={events}
          filters={{ showTasks, showContracts, showSal }}
          canEdit={canEdit}
          onEventOpen={setSelected}
          onDatesChanged={() => {
            invalidate('/calendar');
            invalidate('/tasks');
            loadEvents();
          }}
          key={isFunds ? 'funds' : 'trade'}
        />
      )}

      <EventDetailModal event={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
